import React, { useState, useCallback, Suspense } from 'react'
import { 
  CheckCircle, 
  AlertCircle, 
  DollarSign, 
  Settings, 
  Loader2,
  Calendar,
  MapPin,
  Zap,
  ClipboardList,
  UserCheck,
  Users,
  Radio,
  Truck,
  Sliders,
  Hospital,
  Building2,
  Mail,
  BarChart3,
  FileText,
  History,
  Percent,
  User,
  Wallet
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import Sidebar from '../components/Sidebar'
import UniversalTopBar from '../components/Topbar'
import MaintenanceBanner from '../components/commandcenter/MaintenanceBanner'
import FloatingNavBar from '../components/Navbar'
import ProfilePage from './ProfilePage'
import ProfileScreen from './ProfileScreen'
import CameraScreen from './CameraScreen'
import VehiclesPage from './VehiclesPage'
import DateSelector from '../components/ui/DateSelector'
import DateRangeSelector from '../components/ui/DateRangeSelector'
import Pagination from '../components/ui/Pagination'
import TotalPageNumber from '../components/ui/TotalPageNumber'
import DriversAndVehiclesTab from '../components/fleet/DriversAndVehiclesTab'
import DispatchTab from '../components/fleet/DispatchTab'
import ReportsTab from '../components/fleet/ReportsTab'
import FleetBooking from '../components/fleet/FleetBooking'
import FleetBookingDuplicate from '../components/fleet/FleetBookingDuplicate'
import RatingCard from '../components/fleet/RatingCard'
// Lazy load components for better performance
const HospitalAnalytics = React.lazy(() => import('../components/hospital/HospitalAnalytics'))
const SAdminDashboard = React.lazy(() => import('../components/dashboard/SAdminDashboard'))
const OperatorScreen = React.lazy(() => import('../components/operators/OperatorScreen'))
const AmbulanceInstantBooking = React.lazy(() => import('./AmbulanceInstantBooking'))
const NextPage = React.lazy(() => import('./NextPage'))
const TrackBookings = React.lazy(() => import('../components/ambulance/TrackBookings'))
const BookingHistory = React.lazy(() => import('../components/ambulance/BookingHistory'))
const BookAmbulanceForm = React.lazy(() => import('../components/ambulance/BookAmbulanceForm'))
const FleetInvitationScreen = React.lazy(() => import('../components/fleet/FleetInvitationScreen'))
const FleetBookingList = React.lazy(() => import('../components/fleet/FleetBookingList'))
const FleetBookingTracking = React.lazy(() => import('../components/fleet/FleetBookingTracking'))
const FleetDashboardStats = React.lazy(() => import('../components/fleet/FleetDashboardStats'))
const FleetAssignmentDashboard = React.lazy(() => import('../components/fleet/FleetAssignmentDashboard'))
const FleetDriverAvailability = React.lazy(() => import('../components/fleet/FleetDriverAvailability'))
const FleetPricingOverrides = React.lazy(() => import('../components/fleet/FleetPricingOverrides'))
const AdvPricingOverridePage = React.lazy(() => import('./AdvPricingOverridePage'))
const FleetConfigPage = React.lazy(() => import('./FleetConfigPage'))
const FleetWalletSection = React.lazy(() => import('../components/fleet/wallet/FleetWalletSection'))
import BulkDriverRegistrationModal from '../components/fleet/BulkDriverRegistrationModal'
import DriverRegistrationModal from '../components/fleet/DriverRegistrationModal'
import BulkAmbulanceRegistrationModal from '../components/fleet/BulkAmbulanceRegistrationModal'
import AmbulanceRegistrationModal from '../components/fleet/AmbulanceRegistrationModal'
import PricingConfigModal from '../components/pricing/PricingConfigModal'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../hooks/useWebSocket'
import FleetPartnerHospitals from './FleetPartnerHospitals'

const FleetDashboard = ({ dashboardType = 'fleet' }) => {
  const { user } = useAuth()
  // Initialize WebSocket connection for real-time updates
  const { isConnected, connectionStatus } = useWebSocket()
  const [selectedSidebarItem, setSelectedSidebarItem] = useState('booking')
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('Overview') // For analytics navbar tabs
  const [activeBookingTab, setActiveBookingTab] = useState('Instant Booking') // For booking navbar tabs
  const [currentPage, setCurrentPage] = useState(1) // For pagination
  const [showNextPage, setShowNextPage] = useState(false) // For Next page navigation
  const [itemsPerPage, setItemsPerPage] = useState(20) // For pagination
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false) // Mobile menu state

  // Dynamic date range for Tracking and History
  const [trackingDateFrom, setTrackingDateFrom] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  })
  const [trackingDateTo, setTrackingDateTo] = useState(() => {
    const d = new Date(); d.setHours(23, 59, 59, 999); return d;
  })
  const [historyDateFrom, setHistoryDateFrom] = useState(null)
  const [historyDateTo, setHistoryDateTo] = useState(null)

  // Modal states
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false)
  const [isBulkDriverModalOpen, setIsBulkDriverModalOpen] = useState(false)
  const [isAmbulanceModalOpen, setIsAmbulanceModalOpen] = useState(false)
  const [isBulkAmbulanceModalOpen, setIsBulkAmbulanceModalOpen] = useState(false)
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false)

  // Menu sections for Fleet Dashboard matching Image 2 with unique semantic icons
  const menuSections = [
    {
      title: 'BOOKING & TRACKING',
      items: [
        { id: 'booking', icon: Calendar, label: 'Booking' },
        { id: 'tracking', icon: MapPin, label: 'Tracking' },
        { id: 'instant-booking-duplicate', icon: Zap, label: 'Instant Booking' }
      ]
    },
    {
      title: 'OPERATIONS',
      items: [
        { id: 'assignments', icon: ClipboardList, label: 'Assignments' },
        { id: 'driver-availability', icon: UserCheck, label: 'Driver Availability' },
        { id: 'drivers', icon: Users, label: 'Drivers & Vehicles' },
        { id: 'dispatch', icon: Radio, label: 'Dispatch (static)' },
        { id: 'test', icon: Truck, label: 'All Vehicles' },
        { id: 'fleet_config', icon: Sliders, label: 'Fleet Config' }
      ]
    },
    {
      title: 'HOSPITALS & PARTNERS',
      items: [
        { id: 'partners', icon: Hospital, label: 'Partner Hospitals' },
        { id: 'hospitals', icon: Building2, label: 'Hospital' },
        { id: 'invitations', icon: Mail, label: 'Invitations' }
      ]
    },
    {
      title: 'REPORTS & ANALYTICS',
      items: [
        { id: 'analytics', icon: BarChart3, label: 'Analytics (static)' },
        { id: 'reports', icon: FileText, label: 'Reports (static)' },
        { id: 'history', icon: History, label: 'History' }
      ]
    },
    {
      title: 'PRICING & SETTINGS',
      items: [
        { id: 'adv_pricing_override', icon: Percent, label: 'Margin & Discount' },
        { id: 'wallet', icon: Wallet, label: 'Wallet' },
        { id: 'settings', icon: Settings, label: 'Settings' },
        { id: 'profile', icon: User, label: 'Profile' }
      ]
    }
  ]

  // Memoized sidebar item change handler
  const handleSidebarItemChange = useCallback((itemId) => {
    setSelectedSidebarItem(itemId)
  }, [])

  // Force refresh to clear hot reload cache

  const renderContent = () => {
    // Show Next page if requested
    if (showNextPage) {
      return (
        <Suspense fallback={
          <div className="flex items-center justify-center h-screen">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-gray-600">Loading Next Page...</span>
          </div>
        }>
          <NextPage onBack={() => setShowNextPage(false)} />
        </Suspense>
      )
    }

    switch (selectedSidebarItem) {
      case 'booking':
        return (
          <div className="relative space-y-4 pb-24">
            {/* Booking Content based on active tab */}
            {activeBookingTab === 'Instant Booking' && (
              <div className="space-y-4">
                <FleetBooking />
              </div>
            )}

            {/* Floating Booking Navbar - Fixed at bottom center */}
            {/* COMMENTED OUT: Bottom navigation bar disabled for fleet dashboard ambulance instant booking screen */}
            {/* <FloatingNavBar 
              tabs={['Instant Booking']}
              activeTab="Instant Booking"
              onTabChange={() => {
                setActiveBookingTab('Instant Booking');
              }}
            /> */}
          </div>
        )

      case 'analytics':
        return (
          <div className="relative space-y-3 sm:space-y-4 pb-24">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading analytics...</span>
              </div>
            }>
              <HospitalAnalytics activeTab={activeAnalyticsTab} />
            </Suspense>

            {/* Floating Analytics Navbar - Fixed at bottom center */}
            <FloatingNavBar
              tabs={['Overview', 'Performance', 'Tracking', 'Utilization', 'Financial']}
              activeTab={activeAnalyticsTab}
              onTabChange={setActiveAnalyticsTab}
            />
          </div>
        )

      case 'tracking':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            {/* Top Bar: Date filter card - same UI as Hospital Dashboard Tracking */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2 sm:mb-4">
              <div className="bg-white rounded-xl shadow p-4 flex-1 sm:flex-initial sm:max-w-md">
                <DateRangeSelector
                  fromDate={trackingDateFrom}
                  toDate={trackingDateTo}
                  onRangeChange={(fromDate, toDate) => {
                    setTrackingDateFrom(fromDate || null)
                    setTrackingDateTo(toDate || null)
                  }}
                  showLabels={true}
                  fromLabel="From"
                  toLabel="To"
                />
              </div>
            </div>
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading tracking...</span>
              </div>
            }>
              <FleetBookingTracking dateFrom={trackingDateFrom} dateTo={trackingDateTo} />
            </Suspense>
          </div>
        )

      case 'history':
        return (
          <div className="space-y-3 sm:space-y-4 pb-20">
            {/* Top Bar: Date filter card - same UI as Hospital Dashboard History */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-2 sm:mb-4">
              <div className="bg-white rounded-xl shadow p-4 flex-1 sm:flex-initial sm:max-w-md">
                <DateRangeSelector
                  fromDate={historyDateFrom}
                  toDate={historyDateTo}
                  onRangeChange={(fromDate, toDate) => {
                    setHistoryDateFrom(fromDate || null)
                    setHistoryDateTo(toDate || null)
                  }}
                  showLabels={true}
                  fromLabel="From"
                  toLabel="To"
                />
              </div>
            </div>
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading history...</span>
              </div>
            }>
              <FleetBookingList showHistory={true} dateFrom={historyDateFrom} dateTo={historyDateTo} />
            </Suspense>
          </div>
        )

      case 'assignments':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading assignments...</span>
              </div>
            }>
              <FleetAssignmentDashboard />
            </Suspense>
          </div>
        )

      case 'driver-availability':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading driver availability...</span>
              </div>
            }>
              <FleetDriverAvailability />
            </Suspense>
          </div>
        )

      case 'invitations':
        return (
          <div className="space-y-3 sm:space-y-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading invitations...</span>
              </div>
            }>
              <FleetInvitationScreen />
            </Suspense>
          </div>
        )

      case 'partners':
        return (
          <div className="space-y-3 sm:space-y-4 pb-20">
            <FleetPartnerHospitals />
          </div>
        )

      case 'hospitals':
        return (
          <div className="space-y-3 sm:space-y-4 pb-24">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading hospitals...</span>
              </div>
            }>
              <OperatorScreen />
            </Suspense>
          </div>
        )

      case 'drivers':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <DriversAndVehiclesTab />
          </div>
        )

      case 'wallet':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading wallet...</span>
              </div>
            }>
              <FleetWalletSection />
            </Suspense>
          </div>
        )

      case 'dispatch':
        return (
          <div className="space-y-4">
            <DispatchTab />
          </div>
        )

      case 'reports':
        return (
          <div className="space-y-4">
            <ReportsTab />
          </div>
        )

      // case 'my-pricing':
      //   return (
      //     <div className="space-y-3 sm:space-y-4 pb-4">
      //       <Suspense fallback={
      //         <div className="flex items-center justify-center py-6 sm:py-8">
      //           <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
      //           <span className="text-gray-600 text-sm sm:text-base">Loading Pricing Config...</span>
      //         </div>
      //       }>
      //         <FleetPricingOverrides />
      //       </Suspense>
      //     </div>
      //   )

      case 'adv_pricing_override':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading Margin & Discount...</span>
              </div>
            }>
              <AdvPricingOverridePage />
            </Suspense>
          </div>
        )

      case 'fleet_config':
        return (
          <div className="space-y-3 sm:space-y-4 pb-4">
            <Suspense fallback={
              <div className="flex items-center justify-center py-6 sm:py-8">
                <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                <span className="text-gray-600 text-sm sm:text-base">Loading Fleet Config...</span>
              </div>
            }>
              <FleetConfigPage />
            </Suspense>
          </div>
        )

      case 'settings':
        return (
          <div className="space-y-4 sm:space-y-6">
            {/* Success/Error Messages */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            )}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            {/* Pricing Configuration Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pricing Configuration</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Configure fare calculation, commission rates, and surge pricing for your fleet
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-gray-600" />
                    <h4 className="text-sm font-medium text-gray-700">Pricing Model</h4>
                  </div>
                  <p className="text-xs text-gray-500">
                    Set how fares are calculated: Fixed + Variable, Fixed Only, Variable Only, or Flat Routes
                  </p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="w-4 h-4 text-gray-600" />
                    <h4 className="text-sm font-medium text-gray-700">Commission & Tax</h4>
                  </div>
                  <p className="text-xs text-gray-500">
                    Configure platform commission rates, tax percentages, and who pays the commission
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => setIsPricingModalOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!user?.operator?.operator_id && !user?.operator_id}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Pricing
                </Button>

                {(!user?.operator?.operator_id && !user?.operator_id) && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Operator information required
                  </p>
                )}
              </div>
            </div>

            {/* General Settings Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold mb-4">General Settings</h3>
              <p className="text-sm text-gray-600">
                Additional fleet management settings will be available here.
              </p>
            </div>
          </div>
        )

      case 'profile':
        return (
          <div className="space-y-3 sm:space-y-4">
            <ProfileScreen
              user={{
                name: 'Mathew Norm',
                smartId: 'Smart Id',
                initials: 'MN',
                avatar: null
              }}
              onEditProfile={() => {
                console.log('Edit profile clicked in Fleet Dashboard')
              }}
              onWalletClick={() => {
                console.log('Wallet clicked in Fleet Dashboard')
              }}
              onFeedbackClick={() => {
                console.log('Feedback & Support clicked in Fleet Dashboard')
              }}
              onTermsClick={() => {
                console.log('Terms & Conditions clicked in Fleet Dashboard')
              }}
              onAboutClick={() => {
                console.log('About Us clicked in Fleet Dashboard')
              }}
              onNotificationClick={() => {
                console.log('Notifications clicked in Fleet Dashboard')
              }}
            />
          </div>
        )

      case 'test':
        return (
          // Add Vehicle/Bulk Register already exist as their own real flows
          // in this dashboard (AmbulanceRegistrationModal/
          // BulkAmbulanceRegistrationModal in DriversAndVehiclesTab.jsx) —
          // hidden here the same way HospitalDashboard.jsx already does, so
          // this doesn't duplicate them with a redundant (and, now that
          // Super Admin's copy of this tab does something, differently-
          // scoped) second entry point.
          <VehiclesPage hideDateFilter={true} hideAddVehicle={true} />
        )

      case 'instant-booking-duplicate':
        return (
          <div className="relative space-y-4 pb-24">
            <FleetBookingDuplicate />
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex h-screen" style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)'
    }}>
      {/* Sidebar */}
      <Sidebar
        sections={menuSections}
        selectedItem={selectedSidebarItem}
        onItemClick={setSelectedSidebarItem}
        defaultSelectedItem="booking"
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <MaintenanceBanner />
        {selectedSidebarItem !== 'instant-booking-duplicate' && (
          <div className="px-2 md:px-3 lg:px-3 relative z-30">
            <UniversalTopBar
              title="Fleet Dashboard"
              subtitle="Manage vehicles, drivers, and dispatch operations."
              userRole="fleet"
              onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          </div>
        )}

        <main className="flex-1 overflow-y-auto px-2 md:px-3 lg:px-3 pt-2.5 pb-20 lg:pb-4">
          {/* Dynamic Content based on sidebar selection */}
          {renderContent()}
        </main>
      </div>

      {/* Pricing Configuration Modal */}
      <PricingConfigModal
        operator={{
          id: user?.operator?.operator_id || user?.operator_id,
          name: user?.operator?.name || user?.name || 'Your Fleet'
        }}
        open={isPricingModalOpen}
        onOpenChange={setIsPricingModalOpen}
        onSave={async () => {
          setSuccessMessage('Pricing configuration updated successfully!');
          setTimeout(() => setSuccessMessage(''), 5000);
        }}
      />
    </div>
  )
}

export default FleetDashboard